import { useEffect, useMemo, useRef, useState } from 'react';
import { useOutletContext, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { Tenant } from '@/lib/tenant';
import { fetchServices, fetchStaff, type Service, type Staff } from '@/lib/catalog';
import { fetchSlots, createBooking, type Slot } from '@/lib/booking';
import { ArrowLeft, Check, Clock, Sparkle, User, CalendarBlank } from '@phosphor-icons/react';

// Поток записи: услуга -> мастер -> слот -> контакты -> подтверждение.
// Идемпотентность: idempotency_key генерируется один раз на попытку.
export function BookingFlow() {
  const { tenant } = useOutletContext<{ tenant: Tenant }>();
  const navigate = useNavigate();

  const [service, setService] = useState<Service | null>(null);
  const [staff, setStaff] = useState<Staff | null>(null);
  const [slot, setSlot] = useState<Slot | null>(null);
  const [dayKey, setDayKey] = useState<string | null>(null);
  const [staffPicked, setStaffPicked] = useState(false);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  // Защита от двойного нажатия: синхронный флаг срабатывает раньше, чем
  // React успевает перерисовать кнопку в disabled. Без него два быстрых
  // тапа могут уйти двумя запросами и создать две записи.
  const submitLock = useRef(false);
  // Ключ идемпотентности на текущую попытку записи. Держим его до успеха:
  // при сетевом сбое и повторном нажатии сервер увидит тот же ключ и не
  // создаст вторую запись. После успеха ключ сбрасывается.
  const idemKey = useRef<string>(crypto.randomUUID());

  const { data: services, isLoading: servicesLoading } = useQuery({
    queryKey: ['services', tenant.id],
    queryFn: () => fetchServices(tenant.id)
  });

  const { data: staffList, isLoading: staffLoading } = useQuery({
    queryKey: ['staff', tenant.id, service?.id],
    queryFn: () => fetchStaff(tenant.id, service!.id),
    enabled: Boolean(service)
  });

  const { data: slots, isLoading: slotsLoading } = useQuery({
    queryKey: ['slots', tenant.id, service?.id, staff?.id ?? 'any'],
    queryFn: () => fetchSlots(tenant.id, service!.id, staff?.id),
    enabled: Boolean(service)
  });

  const qc = useQueryClient();

  const mutation = useMutation({
    mutationFn: () =>
      createBooking({
        tenantId: tenant.id,
        serviceId: service!.id,
        staffId: slot!.staffId,
        resourceId: slot!.resourceId,
        startsAt: slot!.startsAt,
        endsAt: slot!.endsAt,
        clientName: name,
        clientPhone: phone,
        idempotencyKey: idemKey.current
      }),
    onSuccess: () => {
      // Слот занят — обновляем кеш, чтобы занятое окно исчезло из списка
      // и при возврате на запись клиент не видел уже забронированное время.
      void qc.invalidateQueries({ queryKey: ['slots'] });
      navigate('/book/success', {
        replace: true,
        state: {
          clientName: name,
          startsAt: slot!.startsAt,
          serviceName: service!.name,
          staffName: slot!.staffName ?? staff?.name ?? null,
          price: service!.price
        }
      });
    },
    onError: () => {
      // Разрешаем повторить попытку после ошибки с тем же ключом
      // идемпотентности — второй записи не будет.
      submitLock.current = false;
    }
  });

  // Одна попытка записи: пока запрос не завершился, повторный запуск
  // не проходит. Сервер дополнительно защищён idempotency_key и EXCLUDE.
  const submitBooking = () => {
    if (submitLock.current || mutation.isPending) return;
    submitLock.current = true;
    mutation.mutate();
  };

  const fmtTime = (iso: string) =>
    new Date(iso).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });

  // Слоты, сгруппированные по дате, чтобы показать день заголовком.
  const slotDays = useMemo(() => {
    if (!slots) return [];
    const map = new Map<string, Slot[]>();
    for (const s of slots) {
      const d = new Date(s.startsAt);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      const list = map.get(key) ?? [];
      list.push(s);
      map.set(key, list);
    }
    return [...map.entries()].map(([key, daySlots]) => {
      // Одна и та же дата/время может прийти от нескольких мастеров
      // (режим «любой мастер»). В списке показываем время один раз,
      // оставляя первый подходящий слот как выбранный.
      const seen = new Set<string>();
      const uniqueSlots = daySlots.filter((s) => {
        const t = new Date(s.startsAt).getTime();
        if (seen.has(String(t))) return false;
        seen.add(String(t));
        return true;
      });
      const first = new Date(uniqueSlots[0].startsAt);
      return {
        key,
        label: first.toLocaleDateString('ru-RU', {
          weekday: 'short',
          day: '2-digit',
          month: 'long'
        }),
        weekday: first.toLocaleDateString('ru-RU', { weekday: 'short' }),
        day: first.toLocaleDateString('ru-RU', { day: '2-digit' }),
        month: first.toLocaleDateString('ru-RU', { month: 'short' }),
        isToday: new Date().toDateString() === first.toDateString(),
        slots: uniqueSlots
      };
    });
  }, [slots]);

  // Выбранный день. Если он ещё не выбран или в нём не осталось слотов —
  // показываем первый доступный.
  const activeDayKey = useMemo(() => {
    if (slotDays.length === 0) return null;
    if (dayKey && slotDays.some((d) => d.key === dayKey)) return dayKey;
    return slotDays[0].key;
  }, [dayKey, slotDays]);

  const activeDay = slotDays.find((d) => d.key === activeDayKey) ?? null;
  const progress = slot ? 4 : staffPicked ? 3 : service ? 2 : 1;

  // Слот принадлежит конкретному мастеру. Если владелец выбрал «любой мастер»,
  // после появления слотов фиксируем мастера из слота — иначе в записи
  // останется null и данные разъедутся с UI.
  useEffect(() => {
    if (!slot) return;
    const fromSlot = staffList?.find((m) => m.id === slot.staffId) ?? null;
    if (fromSlot && fromSlot.id !== staff?.id) setStaff(fromSlot);
  }, [slot, staffList, staff?.id]);

  return (
    <main className="mnc-page mnc-booking-page">
      <header className="mnc-flow-header">
        <Link className="mnc-round-link" to="/" aria-label="Вернуться на главную"><ArrowLeft /></Link>
        <div><span className="mnc-eyebrow">Онлайн-запись</span><h1>{tenant.name}</h1></div>
        <span className="mnc-flow-count">{progress}/4</span>
      </header>

      <div className="mnc-progress" aria-label={`Шаг ${progress} из 4`}>
        {[1, 2, 3, 4].map((step) => <i key={step} className={step <= progress ? 'is-active' : ''} />)}
      </div>

      <section className="mnc-step">
        <h2>
          <span className="mnc-step-num"><Sparkle weight="duotone" /></span>
          Услуга
        </h2>
        {servicesLoading && <p className="mnc-muted">Загружаем услуги…</p>}
        <div className="mnc-service-list">
          {services?.map((s) => (
            <button
              key={s.id}
              className="mnc-card"
              onClick={() => {
                setService(s);
                setStaff(null);
                setStaffPicked(false);
                setSlot(null);
                setDayKey(null);
              }}
              aria-pressed={service?.id === s.id}
            >
              <div className="mnc-card-head">
                <h3>{s.name}</h3>
                <span className="mnc-price">
                  {s.price} ₽ <small>· {s.duration_min} мин</small>
                </span>
              </div>
            </button>
          ))}
        </div>
      </section>

      {service && (
        <section className="mnc-step">
          <h2>
            <span className="mnc-step-num"><User weight="duotone" /></span>
            Мастер
          </h2>
          {staffLoading && <p className="mnc-muted">Загружаем мастеров…</p>}
          {staffList?.length === 0 && (
            <p className="mnc-muted">На эту услугу пока нет доступных мастеров.</p>
          )}
          <div className="mnc-staff-list">
            {staffList && staffList.length > 1 && (
              <button
                className="mnc-staff mnc-staff-any"
                onClick={() => {
                  const randomStaff = staffList[Math.floor(Math.random() * staffList.length)];
                  setStaff(randomStaff);
                  setStaffPicked(true);
                  setSlot(null);
                }}
                aria-label="Выбрать случайного мастера"
                aria-pressed={false}
              >
                <span className="mnc-avatar mnc-avatar-sm">✦</span>
                <span className="mnc-staff-text">
                  <b>Случайный мастер</b>
                  <small>Выберем одного мастера случайно</small>
                </span>
              </button>
            )}
            {staffList?.map((m) => (
              <button
                key={m.id}
                className="mnc-staff"
                onClick={() => {
                  setStaff(m);
                  setStaffPicked(true);
                  setSlot(null);
                }}
                aria-pressed={staff?.id === m.id}
              >
                <span className="mnc-avatar mnc-avatar-sm">{m.name.slice(0, 1)}</span>
                <span className="mnc-staff-text">
                  <b>{m.name}</b>
                  {m.role && <small>{m.role}</small>}
                </span>
              </button>
            ))}
          </div>
        </section>
      )}

      {service && staffPicked && (
        <section className="mnc-step">
          <h2>
            <span className="mnc-step-num"><CalendarBlank weight="duotone" /></span>
            Дата и время
          </h2>
          {slotsLoading && <p className="mnc-muted">Загружаем свободное время…</p>}
          {!slotsLoading && slotDays.length === 0 && (
            <p className="mnc-muted">На ближайшие две недели свободного времени нет.</p>
          )}
          {slotDays.length > 0 && (
            <div className="mnc-days-picker" role="tablist" aria-label="День записи">
              {slotDays.map((day) => (
                <button
                  key={day.key}
                  role="tab"
                  className="mnc-day-chip"
                  aria-selected={day.key === activeDayKey}
                  onClick={() => setDayKey(day.key)}
                >
                  <small>{day.isToday ? 'сегодня' : day.weekday}</small>
                  <b>{day.day}</b>
                  <small>{day.month}</small>
                </button>
              ))}
            </div>
          )}

          {activeDay && (
            <div className="mnc-day">
              <span className="mnc-day-label">{activeDay.label}</span>
              <div className="mnc-slots">
                {activeDay.slots.map((s) => (
                  <button
                    key={s.startsAt}
                    className="mnc-slot"
                    onClick={() => setSlot(s)}
                    aria-pressed={slot?.startsAt === s.startsAt}
                  >
                    {fmtTime(s.startsAt)}
                  </button>
                ))}
              </div>
            </div>
          )}
        </section>
      )}

      {slot && (
        <section className="mnc-step">
          <h2>
            <span className="mnc-step-num"><Check weight="bold" /></span>
            Контакты
          </h2>
          <span className="mnc-steps-done"><Clock weight="duotone" />
            {new Date(slot.startsAt).toLocaleString('ru-RU', {
              day: '2-digit',
              month: 'long',
              hour: '2-digit',
              minute: '2-digit'
            })}
            {(slot.staffName ?? staff?.name) ? ` · ${slot.staffName ?? staff?.name}` : ''} · {service?.price} ₽
          </span>
          <label className="mnc-field">
            Имя
            <input
              placeholder="Как вас зовут"
              value={name}
              autoComplete="name"
              onChange={(e) => setName(e.target.value)}
            />
          </label>
          <label className="mnc-field">
            Телефон
            <input
              placeholder="+7 999 123-45-67"
              inputMode="tel"
              autoComplete="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
          </label>
          <button
            className="mnc-btn mnc-btn-block"
            disabled={!name || phone.replace(/\D/g, '').length < 11 || mutation.isPending}
            onClick={submitBooking}
          >
            {mutation.isPending ? 'Записываем…' : 'Подтвердить запись'}
          </button>
          {mutation.isError && (
            <p className="mnc-muted">Не получилось. Попробуйте ещё раз.</p>
          )}
        </section>
      )}
    </main>
  );
}
